import { brand } from '../../lib/brand';
import { AppearanceToggle } from '../../components/layout/DesignBoundary';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createLoginSchema, type LoginFormData } from './login.schema';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiClient, api } from '../../lib/api.ts';
import { useAuthStore } from '../../store/auth.store.ts';
import { useEditionStore } from '../../store/edition.store.ts';
import type { MyPermissionsDto, LoginResponse, LoginVenueInfo } from '@pokerroom/types';
import { useBrandingByKey } from '../../hooks/useBranding.ts';
import { useSiteName } from '../../hooks/useSiteName';
import { usePwaInstall } from '../../hooks/usePwaInstall';
import { formatDeviceBindingLoginError, requiresVenueSelection } from './device-binding-login';

const LANGUAGE_OPTIONS = [
  { code: 'zh-TW', label: '繁中', flag: '🇹🇼' },
  { code: 'zh-CN', label: '简中', flag: '🇨🇳' },
  { code: 'en', label: 'EN', flag: '🇺🇸' },
] as const;

// ============================================================
// API
// ============================================================
async function loginApi(data: LoginFormData & { venueId?: string }): Promise<LoginResponse> {
  const { totpCode, ...rest } = data;
  const response = await apiClient.post<{
    success: boolean;
    data: LoginResponse;
  }>(
    '/auth/login',
    {
      ...rest,
      ...(totpCode ? { totpCode } : {}),
    },
    {
      // Login is a public endpoint — strip any stale Bearer token
      // that the request interceptor might add from auth store
      transformRequest: [
        (reqData, headers) => {
          if (headers) delete headers['Authorization'];
          return JSON.stringify(reqData);
        },
      ],
      headers: { 'Content-Type': 'application/json' },
    },
  );
  return response.data.data;
}

// ============================================================
// Component
// ============================================================
function LoginPage() {
  const { t, i18n } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const venueOnboarding = searchParams.get('venueOnboarding');
  const { url: loginLogoUrl } = useBrandingByKey('login_logo');
  const siteName = useSiteName();
  const { canInstall, install } = usePwaInstall();

  const handleLangChange = (lang: string) => {
    if (i18n.language === lang) return;
    localStorage.setItem('language', lang);
    window.location.reload();
  };
  const editionStore = useEditionStore();

  useEffect(() => {
    // Clear stale auth state on login page mount to prevent Bearer token interference
    useAuthStore.getState().clearAuth();
    editionStore.fetchEditionInfo().catch(() => {});
  }, []);

  const setAuth = useAuthStore((s) => s.setAuth);
  const setPermissions = useAuthStore((s) => s.setPermissions);
  const [showTotp, setShowTotp] = useState(false);
  const [serverError, setServerError] = useState<string | null>(() => {
    const reason = new URLSearchParams(window.location.search).get('session');
    if (reason === 'revoked' || reason === 'expired') return t('sessionInvalid');
    const recovery = new URLSearchParams(window.location.search).get('device');
    return recovery === 'revoked' || recovery === 'denied'
      ? (formatDeviceBindingLoginError({ errorCode: 'DEVICE_BINDING_REVOKED' }) ?? null)
      : null;
  });
  const [venueSelection, setVenueSelection] = useState<{
    venues: LoginVenueInfo[];
    venueSelectToken: string;
  } | null>(null);

  const loginSchema = createLoginSchema(t);
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: '', password: '', totpCode: '' },
  });

  const completeLogin = async (data: LoginResponse) => {
    setAuth(data);
    let firstPage = '/';
    try {
      const perms = await api.get<MyPermissionsDto>('/auth/my-permissions');
      setPermissions(perms.visiblePages, perms.allowedActions);
      // Navigate to first permitted page if dashboard is not visible
      if (perms.visiblePages?.length && !perms.visiblePages.includes('/')) {
        firstPage = perms.visiblePages[0] ?? '/';
      }
    } catch {
      // Non-blocking
    }
    useEditionStore.getState().fetchEditionInfo().catch(console.warn);

    if (data.requiresTotpSetup) {
      navigate('/auth/setup-2fa');
    } else if (data.user.mustChangePassword) {
      navigate('/auth/change-password');
    } else {
      navigate(
        venueOnboarding && /^[0-9a-f-]{36}$/i.test(venueOnboarding)
          ? `/admin/venues?onboarding=${encodeURIComponent(venueOnboarding)}`
          : firstPage,
      );
    }
  };

  const loginMutation = useMutation({
    mutationFn: loginApi,
    onSuccess: async (data) => {
      // Multi-venue: show venue picker
      if (requiresVenueSelection(data)) {
        useAuthStore.getState().clearAuth();
        setVenueSelection({
          venues: data.venues,
          venueSelectToken: data.venueSelectToken,
        });
        setServerError(null);
        return;
      }

      await completeLogin(data);
    },
    onError: (error: {
      response?: {
        data?: { message?: string; errorCode?: string; requestRef?: string; expiresAt?: string };
      };
    }) => {
      const errorCode = error.response?.data?.errorCode;
      const rawMessage = error.response?.data?.message ?? '';
      const bindingError = formatDeviceBindingLoginError(error.response?.data);

      if (errorCode === 'TOTP_REQUIRED') {
        setShowTotp(true);
        setServerError(null);
      } else if (bindingError) {
        setShowTotp(false);
        setServerError(bindingError);
      } else {
        setShowTotp(false);
        setServerError(rawMessage || t('loginFailedRetry'));
      }
    },
  });

  const venueSelectMutation = useMutation({
    mutationFn: async (venueId: string) => {
      if (!venueSelection) throw new Error('No venue selection state');
      const response = await apiClient.post<{
        success: boolean;
        data: LoginResponse;
      }>(
        '/auth/select-venue',
        {
          venueSelectToken: venueSelection.venueSelectToken,
          venueId,
        },
        {
          headers: { Authorization: undefined },
        },
      );
      return response.data.data;
    },
    onSuccess: async (data) => {
      setVenueSelection(null);
      await completeLogin(data);
    },
    onError: (error: {
      response?: {
        data?: { message?: string; errorCode?: string; requestRef?: string; expiresAt?: string };
      };
    }) => {
      const response = error.response?.data;
      const bindingError = formatDeviceBindingLoginError(response);
      if (bindingError) {
        setServerError(bindingError);
      } else {
        setServerError(response?.message ?? t('loginFailedRetry'));
      }
    },
  });

  const onSubmit = (data: LoginFormData) => {
    setServerError(null);
    loginMutation.mutate({
      ...data,
      totpCode: data.totpCode?.trim() || undefined,
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-base-100 py-8">
      <div className="w-full max-w-md px-6">
        <div className="flex justify-end mb-4">
          <AppearanceToggle />
        </div>
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          {loginLogoUrl ? (
            <img src={loginLogoUrl} alt={siteName} className="h-20 mx-auto mb-2 object-contain" />
          ) : (
            <>
              <img
                src={brand.logo}
                alt={siteName}
                className="zipa-logo-light h-24 mx-auto mb-2 object-contain"
              />
              <img
                src={brand.logoDark}
                alt={siteName}
                className="zipa-logo-dark h-24 mx-auto mb-2 object-contain"
              />
            </>
          )}
          <p className="text-base-content/60 mt-2 text-sm">{t('subtitle')}</p>
          {editionStore.isLoaded && editionStore.isLite() && (
            <span className="inline-block mt-2 px-3 py-0.5 rounded-full text-xs font-medium bg-base-300 text-base-content/50 tracking-wide">
              Lite Edition
            </span>
          )}
        </div>

        {/* Venue Selection */}
        {venueSelection ? (
          <div className="bg-base-200 rounded-2xl p-8 shadow-lg border border-base-300">
            <h2 className="text-xl font-semibold text-base-content mb-2">
              {t('selectVenue') || 'Select Venue'}
            </h2>
            <p className="text-sm text-base-content/60 mb-6">
              {t('selectVenueHint') || 'Choose a venue to continue'}
            </p>

            <div className="space-y-3">
              {venueSelection.venues.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  disabled={venueSelectMutation.isPending}
                  onClick={() => venueSelectMutation.mutate(v.id)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-base-300 border border-base-300 hover:border-primary hover:bg-base-300/80 transition disabled:opacity-50 group"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-lg">🏢</span>
                    <div className="text-left">
                      <div className="font-medium text-base-content">{v.name}</div>
                      <div className="text-xs text-base-content/50">{v.code}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        v.edition === 'BUSINESS'
                          ? 'bg-success/20 text-success'
                          : v.edition === 'ENTERPRISE'
                            ? 'bg-primary/20 text-primary'
                            : 'bg-base-content/10 text-base-content/50'
                      }`}
                    >
                      {v.edition}
                    </span>
                    {v.isDefault && <span className="text-xs text-primary">Default</span>}
                  </div>
                </button>
              ))}
            </div>

            {serverError && (
              <div className="mt-4 rounded-lg bg-error/10 border border-error/30 px-4 py-3 text-sm text-error">
                {serverError}
              </div>
            )}

            <button
              type="button"
              onClick={() => {
                setVenueSelection(null);
                setServerError(null);
              }}
              className="mt-4 w-full py-2 text-sm text-base-content/50 hover:text-base-content transition"
            >
              {t('backToLogin') || 'Back to Login'}
            </button>
          </div>
        ) : (
          /* Login Card */
          <div className="bg-base-200 rounded-2xl p-8 shadow-lg border border-base-300">
            <h2 className="text-xl font-semibold text-base-content mb-6">{t('loginSystem')}</h2>

            {venueOnboarding && (
              <p role="status" className="text-sm mb-4">
                {t('admin:venue.reauthenticate')}
              </p>
            )}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              {/* Username */}
              <div>
                <label className="block text-sm font-medium text-base-content/80 mb-1.5">
                  {t('usernameLabel')}
                </label>
                <input
                  {...register('username')}
                  type="text"
                  autoComplete="username"
                  className="w-full px-4 py-3 rounded-lg bg-base-300 border border-base-300 text-base-content placeholder:text-base-content/40 focus:outline-hidden focus:border-primary focus:ring-1 focus:ring-primary transition"
                  placeholder={t('usernamePlaceholder')}
                />
                {errors.username && (
                  <p className="mt-1 text-sm text-error">{errors.username.message}</p>
                )}
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-base-content/80 mb-1.5">
                  {t('password')}
                </label>
                <input
                  {...register('password')}
                  type="password"
                  autoComplete="current-password"
                  className="w-full px-4 py-3 rounded-lg bg-base-300 border border-base-300 text-base-content placeholder:text-base-content/40 focus:outline-hidden focus:border-primary focus:ring-1 focus:ring-primary transition"
                  placeholder={t('passwordPlaceholder')}
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-error">{errors.password.message}</p>
                )}
              </div>

              {/* TOTP（動態顯示） */}
              {showTotp && (
                <div>
                  <label className="block text-sm font-medium text-base-content/80 mb-1.5">
                    {t('twoStepCode')}
                  </label>
                  <input
                    {...register('totpCode')}
                    type="text"
                    inputMode="text"
                    autoComplete="one-time-code"
                    autoCapitalize="characters"
                    spellCheck={false}
                    onPaste={(event) => {
                      const code = loginSchema.shape.totpCode.safeParse(
                        event.clipboardData.getData('text'),
                      );
                      if (code.success && code.data) {
                        event.preventDefault();
                        setValue('totpCode', code.data, { shouldValidate: true });
                      }
                    }}
                    maxLength={8}
                    className="w-full px-4 py-3 rounded-lg bg-base-300 border border-base-300 text-base-content placeholder:text-base-content/40 focus:outline-hidden focus:border-primary focus:ring-1 focus:ring-primary transition tracking-widest text-center text-lg timer-text"
                    placeholder="000000"
                  />
                  <p className="mt-1 text-xs text-base-content/50">{t('totpHint')}</p>
                  {errors.totpCode && (
                    <p className="mt-1 text-sm text-error">{errors.totpCode.message}</p>
                  )}
                </div>
              )}

              {/* Server Error */}
              {serverError && (
                <div className="rounded-lg bg-error/10 border border-error/30 px-4 py-3 text-sm text-error">
                  {serverError}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loginMutation.isPending}
                className="w-full py-3 px-6 rounded-lg bg-primary text-primary-content font-semibold hover:bg-primary transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loginMutation.isPending ? t('loggingIn') : t('login')}
              </button>
            </form>
          </div>
        )}

        {/* Language switcher — below login card */}
        <div className="flex items-center justify-center gap-1 mt-6">
          {LANGUAGE_OPTIONS.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => handleLangChange(lang.code)}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs transition-colors ${
                i18n.language === lang.code
                  ? 'bg-primary text-primary-content font-medium'
                  : 'bg-base-300 text-base-content/60 hover:text-base-content hover:bg-base-300'
              }`}
            >
              <span>{lang.label}</span>
            </button>
          ))}
        </div>

        <p className="mt-4 text-center text-xs text-base-content/50">
          {t('version')} · v{__APP_VERSION__}
        </p>

        {/* PWA Install */}
        {canInstall && (
          <button
            type="button"
            onClick={install}
            className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-base-200 border border-base-300 text-sm text-base-content/70 hover:text-base-content hover:border-primary transition"
          >
            <span className="material-symbols-outlined text-lg">download</span>
            {t('installApp') || 'Install App'}
          </button>
        )}
      </div>
    </div>
  );
}

export default LoginPage;
